import { type ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { IconDots } from "@tabler/icons-react";
import NotepadIcon from "../../assets/icons/notepad.svg?react";
import StarsIcon from "../../assets/icons/stars.svg?react";
import ShareBtn from "../../assets/icons/share-btn.svg";
import { displayToast } from "../../components/app/AppToast";
import ContentHeader from "../../components/app/headers/ContentHeader";
import SaveHeader from "../../components/app/headers/SaveHeader";
import BottomPanel from "../../components/app/panels/BottomPanel";
import { PanelContainer, PanelRow } from "../../components/app/panels/layout";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";

import VideoCard from "../../components/cards/VideoCard";
import CustomIcon from "../../components/CustomIcon";
import { Input } from "../../components/ui/Input";
import Loader from "../../components/Loader";
import SharedBySheet from "../../components/SharedBySheet";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";

import { useSharePlaylistQr } from "../../domains/playlist/hooks/useSharePlaylistQr";
import { useCheckUserAccessRegistered } from "../../domains/user/hooks/useCheckUserAccessRegistered";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useGetUserCreatedPlaylistsQuery } from "../../domains/user/hooks/useGetUserCreatedPlaylistsQuery";
import { apiClient, useApiQuery, useApiRevalidate } from "../../services/api";

import { useTranslation } from "react-i18next";
import { FLAG_PLAYLIST_SUMMARY } from "../../config/featureFlags";
import { userNoteLabel } from "../../../../api/src/shared/domain/language";
import type { SupportedLanguage } from "../../../../api/src/shared/domain/language";
import PlaylistPageHeader from "../playlist/PlaylistPageHeader";
import { goBackInApp } from "../../domains/app/utils/backNavigation";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { DesktopPlaylistPage } from "../playlist/desktop/DesktopPlaylistPage";

function PlaylistNoteEditor({
  playlistId,
  description,
  descriptionLabel,
  firstName,
  language,
  onClose,
  t,
}: {
  playlistId: string;
  description: string | null;
  descriptionLabel: string | null;
  firstName: string | null;
  language: SupportedLanguage;
  onClose: () => void;
  t: (key: string) => string;
}) {
  const revalidate = useApiRevalidate();
  const defaultLabel =
    firstName !== null && firstName !== ""
      ? userNoteLabel(firstName, language)
      : null;
  const [value, setValue] = useState(description !== null ? description : "");
  const [label, setLabel] = useState(
    descriptionLabel !== null
      ? descriptionLabel
      : defaultLabel !== null
        ? defaultLabel
        : ""
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiClient.patch("/webapp/private/playlists/:playlistId/summary", {
        param: { playlistId },
        json: {
          description: value !== "" ? value : null,
          descriptionLabel: value !== "" ? (label !== "" ? label : null) : null,
        },
      });
      await revalidate("/webapp/private/playlists/me");
      onClose();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="px-5 py-4 flex flex-col gap-2">
      <input
        value={label}
        onChange={(e) => {
          setLabel(e.target.value);
        }}
        placeholder={
          defaultLabel !== null ? defaultLabel : t("Label (optional)")
        }
        className="w-full rounded-ds-button-sm border border-[#E0E0E0] px-[12px] py-[6px] text-footnote/normal font-semibold  font-['DM_Sans',sans-serif] outline-none focus:border-[#B0B0B0] text-[#303030]"
      />
      <textarea
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        rows={3}
        placeholder={t("Add a note about this collection...")}
        className="w-full rounded-ds-button-md border border-[#E0E0E0] p-[12px] text-[14px] leading-normal font-['DM_Sans',sans-serif] resize-none outline-none focus:border-[#B0B0B0]"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-ds-button-sm px-3 py-[6px] text-[13px] font-medium bg-[#303030] text-white border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSaving ? t("Saving...") : t("Save")}
        </button>
        {value !== "" && (
          <button
            type="button"
            onClick={() => {
              setValue("");
            }}
            className="rounded-ds-button-sm px-3 py-[6px] text-[13px] font-medium text-[#E53935] bg-transparent border-none cursor-pointer"
          >
            {t("Clear")}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded-ds-button-sm px-3 py-[6px] text-[13px] font-medium text-[#8A8A8A] bg-transparent border-none cursor-pointer"
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
}

function PlaylistSummaryEditor({
  playlistId,
  summary,
  summarySource,
  articleCount,
  onClose,
  t,
}: {
  playlistId: string;
  summary: string | null;
  summarySource: string | null;
  articleCount: number;
  onClose: () => void;
  t: (key: string) => string;
}) {
  const revalidate = useApiRevalidate();
  const [value, setValue] = useState(summary !== null ? summary : "");
  const [source, setSource] = useState<"ai" | "manual">(
    summarySource === "ai" || summarySource === "manual"
      ? summarySource
      : "manual"
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleGenerate = async () => {
    if (articleCount > 20) {
      displayToast(
        t("Cannot generate a summary for more than 20 articles"),
        "warning"
      );
      return;
    }
    setIsGenerating(true);
    try {
      const res = await apiClient.post(
        "/webapp/private/playlists/:playlistId/generate-summary",
        { param: { playlistId } }
      );
      setValue(res.body.summary);
      setSource("ai");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiClient.patch("/webapp/private/playlists/:playlistId/summary", {
        param: { playlistId },
        json: {
          summary: value !== "" ? value : null,
          isAiGenerated: source === "ai" && value !== "",
        },
      });
      await revalidate("/webapp/private/playlists/me");
      onClose();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      await apiClient.patch("/webapp/private/playlists/:playlistId/summary", {
        param: { playlistId },
        json: { summary: null },
      });
      await revalidate("/webapp/private/playlists/me");
      onClose();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="px-5 py-4 flex flex-col gap-2">
      <textarea
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setSource("manual");
        }}
        rows={4}
        placeholder={t("Write a summary about this collection...")}
        className="w-full rounded-ds-button-md border border-[#E0E0E0] p-[12px] text-[14px] leading-normal font-['DM_Sans',sans-serif] resize-none outline-none focus:border-[#B0B0B0]"
      />
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={articleCount === 0 || articleCount > 20 || isGenerating}
          className="flex items-center gap-1 rounded-ds-button-sm px-3 py-[6px] text-[13px] font-medium border border-[#E0E0E0] bg-white cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <StarsIcon className="size-4" />
          {isGenerating ? t("Generating...") : t("Generate AI Summary")}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || isGenerating}
          className="rounded-ds-button-sm px-3 py-[6px] text-[13px] font-medium bg-[#303030] text-white border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSaving ? t("Saving...") : t("Save")}
        </button>
        {value !== "" && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={isSaving || isGenerating}
            className="rounded-ds-button-sm px-3 py-[6px] text-[13px] font-medium text-[#E53935] bg-transparent border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("Delete")}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded-ds-button-sm px-3 py-[6px] text-[13px] font-medium text-[#8A8A8A] bg-transparent border-none cursor-pointer"
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
}

export default function ProfilePlaylistPage(props: {
  playlistId: string;
  userId?: string;
}) {
  if (isDesktopLayout()) {
    // Keyed by the collection, as the public collection page is.
    return (
      <DesktopPlaylistPage
        key={props.playlistId}
        id={props.playlistId}
        roomId={null}
      />
    );
  }
  return <MobileProfilePlaylistPage playlistId={props.playlistId} />;
}

function MobileProfilePlaylistPage({ playlistId }: { playlistId: string }) {
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const { playlists, isLoading: isOwnedLoading } =
    useGetUserCreatedPlaylistsQuery();
  const ownedPlaylist = playlists.find((p) => p.id === playlistId) ?? null;
  const sharedQuery = useApiQuery(
    "/webapp/public/playlists/:playlistId",
    !isOwnedLoading && ownedPlaylist === null
      ? { param: { playlistId } }
      : false
  );
  const sharedPlaylist =
    sharedQuery.state === "success" ? sharedQuery.body : null;
  const playlist = ownedPlaylist ?? sharedPlaylist;
  const isMe =
    playlist === null
      ? false
      : currentUser !== null && playlist.user?.id === currentUser.id;
  // QR sharing is reserved to field-force MSLs (members of at least one MSL
  // room), mirroring the dashboard's field-force playlist sharing.
  const isFieldForceMsl =
    currentUser !== null && currentUser.mslRooms.length > 0;
  const [showSheet, setShowSheet] = useState(false);
  const revalidate = useApiRevalidate();
  const [editMode, setEditMode] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    triggerShare: openShareSheet,
    triggerNativeShare,
    renderShareSheet,
  } = useSharePlaylistQr(
    playlist,
    currentUser === null ? null : currentUser.id
  );
  const [title, setTitle] = useState(playlist?.title ?? "");

  useCheckUserAccessRegistered({ memorizePath: true });

  useEffect(() => {
    if (playlist !== null) {
      setTitle(playlist.title);
    }
  }, [playlist]);

  useEffect(() => {
    initialize();
  }, []);

  useEffect(() => {
    if (sharedQuery.state === "error") {
      displayToast(t("Something went wrong. Please try again."), "error");
      history.replace("/profile");
    }
  }, [sharedQuery.state, history, t]);

  async function initialize() {
    try {
      await reloadCurrentUser();
    } catch (error) {
      displayToast(t("Something went wrong. Please try again."), "error");
      console.error("Error during initialization", error);
      history.replace("/profile");
    }
  }

  async function handleSubmit() {
    try {
      if (playlist === null) return;
      if (isMe) {
        await apiClient.post("/webapp/private/playlists/:playlistId/rename", {
          param: { playlistId },
          json: { title },
        });
        await revalidate("/webapp/private/playlists/me");
        await reloadCurrentUser();
        displayToast(t("Collection renamed"), "success");
        setEditMode(false);
      }
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  async function handleDeletePlaylist() {
    if (playlist === null) return;
    if (isMe) {
      setIsDeleting(true);
      try {
        await apiClient.delete("/webapp/private/playlists/:playlistId", {
          param: { playlistId },
        });
        await revalidate("/webapp/private/playlists/me");
        await reloadCurrentUser();
        history.replace("/profile?mainTab=library");
        displayToast(t("Collection deleted"), "success");
      } catch {
        displayToast(t("Something went wrong. Please try again."), "error");
        setIsDeleting(false);
      }
    }
  }

  const articleIds = [...new Set(playlist?.playlist)];
  const contentIds = [...new Set(playlist?.playlistContent)];
  const batchQuery = useApiQuery(
    "/webapp/public/content/batch-mixed",
    articleIds.length === 0 && contentIds.length === 0
      ? false
      : {
          query: {
            articleIds: articleIds.join(","),
            contentIds: contentIds.join(","),
          },
        }
  );

  if (playlist === null) return <Loader expanded />;

  const playlistContent =
    batchQuery.state === "success"
      ? [...batchQuery.body.articles, ...batchQuery.body.contents]
      : [];

  // Note/summary are a staging-only feature: hide their read-only display when
  // the flag is off by masking the fields PlaylistPageHeader renders on.
  const headerPlaylist = FLAG_PLAYLIST_SUMMARY
    ? playlist
    : {
        ...playlist,
        description: null,
        descriptionLabel: null,
        summary: null,
        summarySource: null,
      };

  return (
    <ScrollableLayout
      background="var(--color-ds-white)"
      header={
        editMode ? (
          <SaveHeader
            title={t("Edit playlist")}
            saveReady
            onSaveClick={handleSubmit}
          />
        ) : (
          <ContentHeader
            backLabel={t("Back")}
            onBack={() => {
              // A push, a chat reference or a shared link can open this page
              // with no page of ours behind it.
              goBackInApp(history, "/profile");
            }}
            actions={
              <button
                type="button"
                onClick={() => {
                  setShowSheet(true);
                }}
                title={t("More")}
                className="inline-flex cursor-pointer items-center justify-center"
              >
                <IconDots className="size-6 text-primary-black" />
              </button>
            }
          />
        )
      }
    >
      <div className="mx-auto w-full md:max-w-[760px] md:px-10">
        {editMode ? (
          <div className="p-[40px_20px] flex items-center justify-between gap-[16px]">
            <Input
              value={title}
              onInput={(e: ChangeEvent<HTMLInputElement>) => {
                setTitle(e.target.value);
              }}
              placeholder={t("Playlist name")}
            />
          </div>
        ) : (
          <PlaylistPageHeader
            playlist={headerPlaylist}
            articleCount={playlistContent.length}
            t={t}
            desktopActions={
              <button
                type="button"
                onClick={() => {
                  setShowSheet(true);
                }}
                title={t("More")}
                className="hidden cursor-pointer items-center justify-center md:inline-flex"
              >
                <img src={ShareBtn} alt="" className="size-8" />
              </button>
            }
          />
        )}

        {!editMode &&
          (playlistContent.length === 0 ? (
            <p className="my-4 text-center">
              {t("Save your favorite content in this playlist")}
            </p>
          ) : (
            <div
              className="flex w-full flex-col divide-y divide-ds-slate-100 px-4 pt-4 *:py-4"
              style={{ paddingBottom: "calc(16px + var(--safe-area-bottom))" }}
            >
              {playlistContent.map((content) => {
                switch (content.content_format) {
                  case "Article": {
                    return (
                      <ArticlePublicationCard
                        roomId={null}
                        key={content.id + "--profile-card-key"}
                        article={content}
                        analyticsListName={AnalyticsItemList.PLAYLIST_PAGE}
                        recommendation={null}
                        onClick={null}
                        className="bg-transparent"
                        showImage={true}
                      />
                    );
                  }
                  case "Nectar": {
                    return null;
                  }
                  case "Video": {
                    return (
                      <VideoCard
                        roomId={null}
                        key={content.id + "--profile-card-key"}
                        video={content}
                        analyticsListName={AnalyticsItemList.PLAYLIST_PAGE}
                        recommendation={null}
                        onClick={null}
                        className={null}
                      />
                    );
                  }
                  case "Infographic":
                  case "Audio": {
                    return null;
                  }
                  default: {
                    throw new Error(content satisfies never);
                  }
                }
              })}
            </div>
          ))}
      </div>

      <SharedBySheet type="playlist" />

      {FLAG_PLAYLIST_SUMMARY && isMe && (
        <>
          <BottomPanel
            isOpen={editingNote}
            onClose={() => {
              setEditingNote(false);
            }}
            title={t("Edit note")}
            displayCloseButton
          >
            {editingNote && (
              <PlaylistNoteEditor
                playlistId={playlistId}
                description={playlist.description}
                descriptionLabel={playlist.descriptionLabel}
                firstName={currentUser === null ? null : currentUser.firstname}
                language={playlist.language}
                onClose={() => {
                  setEditingNote(false);
                }}
                t={t}
              />
            )}
          </BottomPanel>

          <BottomPanel
            isOpen={editingSummary}
            onClose={() => {
              setEditingSummary(false);
            }}
            title={t("Edit AI summary")}
            displayCloseButton
          >
            {editingSummary && (
              <PlaylistSummaryEditor
                playlistId={playlistId}
                summary={playlist.summary}
                summarySource={playlist.summarySource}
                articleCount={articleIds.length}
                onClose={() => {
                  setEditingSummary(false);
                }}
                t={t}
              />
            )}
          </BottomPanel>
        </>
      )}

      <BottomPanel
        isOpen={showSheet}
        onClose={() => {
          setShowSheet(false);
        }}
      >
        <PanelContainer>
          {/* EDIT NAME */}
          {isMe && (
            <PanelRow
              style={{ opacity: isDeleting ? 0.5 : 1 }}
              onClick={() =>
                !isDeleting && (setEditMode(true), setShowSheet(false))
              }
            >
              <CustomIcon
                iconName="edit_pen"
                color="var(--color-primary-black)"
              />
              <div className="text">
                <span className="label">{t("Edit playlist name")}</span>
                <span className="description">
                  {t("Change the playlist name")}
                </span>
              </div>
            </PanelRow>
          )}

          {/* EDIT NOTE */}
          {FLAG_PLAYLIST_SUMMARY && isMe && (
            <PanelRow
              style={{ opacity: isDeleting ? 0.5 : 1 }}
              onClick={() =>
                !isDeleting && (setEditingNote(true), setShowSheet(false))
              }
            >
              <NotepadIcon className="text-[#8A8A8A]" />
              <div className="text">
                <span className="label">{t("Edit note")}</span>
                <span className="description">
                  {t("Add a note about this collection")}
                </span>
              </div>
            </PanelRow>
          )}

          {/* EDIT AI SUMMARY */}
          {FLAG_PLAYLIST_SUMMARY && isMe && (
            <PanelRow
              style={{ opacity: isDeleting ? 0.5 : 1 }}
              onClick={() =>
                !isDeleting && (setEditingSummary(true), setShowSheet(false))
              }
            >
              <StarsIcon />
              <div className="text">
                <span className="label">{t("Edit AI summary")}</span>
                <span className="description">
                  {t("Write or generate a summary")}
                </span>
              </div>
            </PanelRow>
          )}

          {/* SHARE — field-force MSL owners get the QR sheet (download + scan
              to add); everyone else gets the plain native share. */}
          {currentUser === null ? null : (
            <PanelRow
              style={{ opacity: isDeleting ? 0.5 : 1 }}
              onClick={() => {
                if (!isDeleting) {
                  if (isMe && isFieldForceMsl) {
                    void openShareSheet();
                  } else {
                    void triggerNativeShare();
                  }
                  setShowSheet(false);
                }
              }}
            >
              <CustomIcon
                iconName="share-alt"
                color="var(--color-primary-black)"
              />
              <div className="text">
                <span className="label">{t("Share this playlist")}</span>
                <span className="description">
                  {t("Share this playlist to other people")}
                </span>
              </div>
            </PanelRow>
          )}
          {isMe && (
            <PanelRow
              style={{ opacity: isDeleting ? 0.5 : 1 }}
              onClick={handleDeletePlaylist}
            >
              <CustomIcon iconName="trash" color="var(--color-primary-black)" />
              <div className="text">
                <span className="label">{t("Delete playlist")}</span>
                <span className="description">
                  {t("Permanently delete this playlist")}
                </span>
              </div>
            </PanelRow>
          )}
        </PanelContainer>
      </BottomPanel>

      {renderShareSheet()}
    </ScrollableLayout>
  );
}
